/**
 * PostHog analytics helper — funnel instrumentation only.
 *
 * The PostHog SDK is loaded by the snippet in `public/index.html` and exposed
 * on `window.posthog`. We never call `posthog.init()` from React; we only
 * `capture()` events through this thin wrapper.
 *
 * Guarantees:
 *   - If `window.posthog` is not present (SDK not yet loaded, blocked by an
 *     ad-blocker, …) → silent no-op. The PostHog snippet stubs `capture`
 *     into a buffer until the real SDK arrives, so this branch is rarely
 *     hit, but we stay defensive.
 *   - If `capture(...)` throws (unexpected) → swallowed. Analytics must
 *     NEVER break the user funnel.
 *   - No PII (name / email / member_code / customer_id / phone) is ever
 *     attached here. Callers are responsible for keeping their props clean;
 *     this helper is intentionally pass-through on props.
 */
export function safeCapture(eventName, props) {
  try {
    if (typeof window === 'undefined') return;
    const ph = window.posthog;
    if (!ph || typeof ph.capture !== 'function') return;
    ph.capture(eventName, props || {});
  } catch (err) {
    // Never propagate analytics failures into the React tree.
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[analytics] safeCapture failed for', eventName, err);
    }
  }
}
